import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const ArithmeticMeanCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [values, setValues] = useState(['10', '20', '30', '40', '50']);
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const numbers = values
      .filter(v => v.trim() !== '')
      .map(v => parseFloat(parseBrazilianNumber(v)))
      .filter(n => !isNaN(n));

    if (numbers.length === 0) return;

    const sum = numbers.reduce((acc, val) => acc + val, 0);
    const mean = sum / numbers.length;

    const sorted = [...numbers].sort((a, b) => a - b);
    const median = sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];

    const min = Math.min(...numbers);
    const max = Math.max(...numbers);
    const range = max - min;

    const variance = numbers.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / numbers.length;
    const stdDeviation = Math.sqrt(variance);

    const res = { mean, sum, count: numbers.length, median, min, max, range, variance, stdDeviation, numbers };
    setResult(res);

    onResultChange([
      { label: 'Média Aritmética', value: res.mean.toFixed(2) },
      { label: 'Soma Total', value: res.sum.toFixed(2) },
      { label: 'Quantidade', value: res.count.toString() },
      { label: 'Mediana', value: res.median.toFixed(2) }
    ]);
  };

  const handleValueChange = (index: number, newValue: string) => {
    const newValues = [...values];
    newValues[index] = newValue;
    setValues(newValues);
  };

  const addValue = () => setValues([...values, '']);
  const removeValue = (index: number) => {
    if (values.length > 2) setValues(values.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Média Aritmética</h2>
        <p className="text-theme-muted mb-4">Calcule média, mediana e outras estatísticas de um conjunto de valores.</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold">Valores</h3>
          <button onClick={addValue} className="px-4 py-2 bg-accent-gradient text-white rounded-lg font-bold hover:scale-105 transition-all">Adicionar</button>
        </div>
        <div className="space-y-4">
          {values.map((v, i) => (
            <div key={i} className="flex gap-3">
              <FormField label={`Valor ${i + 1}`} value={v} onChange={(val) => handleValueChange(i, val)} isNumeric type="text" />
              {values.length > 2 && (
                <button onClick={() => removeValue(i)} className="px-4 py-3 bg-rose-600 text-white rounded-xl self-end">Remover</button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <button onClick={calculate} className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:scale-105 transition-all">Calcular</button>
        </div>
      </div>

      {result && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ResultCard title="Média" value={result.mean.toFixed(2)} color="indigo" />
          <ResultCard title="Mediana" value={result.median.toFixed(2)} color="emerald" />
          <ResultCard title="Desvio Padrão" value={result.stdDeviation.toFixed(2)} color="amber" />
          <ResultCard title="Amplitude" value={result.range.toFixed(2)} color="purple" />
        </div>
      )}

      <EducationalBlock title="Estatística" tips={["A média é a soma dividida pela quantidade.", "A mediana é o valor central.", "O desvio padrão mede a dispersão."]} />
    </div>
  );
};

export default ArithmeticMeanCalculator;
